import type { ComponentProps, ReactNode } from 'react';

import type { PortIndicator, PortSpeed, PortStatus } from '../Port/utils';
import type { SlotChipProps, SlotChipTier } from './atoms/SlotChip';
import { outlines } from '../../common/outlines';
import { transitions } from '../../common/transitions';
import { BadgeGroup } from '../../components/Badge/BadgeGroup';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { useChassisTraceHighlight } from '../Chassis/atoms/ChassisTraces';
import { chassisVars } from '../Chassis/atoms/utils';
import { SlotCaption } from './atoms/SlotCaption';
import { SlotChip } from './atoms/SlotChip';
import { hasRailIndicators, SlotIndicators } from './atoms/SlotIndicators';
import { slotVars } from './atoms/utils';

export type { SlotCaptionProps } from './atoms/SlotCaption';
export { SlotCaption } from './atoms/SlotCaption';
export type {
  SlotChipPreview,
  SlotChipPreviewPayload,
  SlotChipProps,
  SlotChipTier,
  SlotChipVariant,
} from './atoms/SlotChip';
export { SlotChip, SlotChipPreviewCard, SlotChipPreviewContent } from './atoms/SlotChip';
export type { SlotIndicatorsProps } from './atoms/SlotIndicators';
export { SlotIndicatorIcon, SlotIndicators } from './atoms/SlotIndicators';
export { slotVars } from './atoms/utils';

const SlotDiagram = styled('div', {
  zIndex: slotVars.zIndex.diagram,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Full slot width so the glyph centres in it — the recess is wider than the 24px art.
  widthAll: chassisVars.slot.width,
  minHeight: `$${chassisVars.slot.diagram}`,
  padding: '$2 0',
});
SlotDiagram.displayName = 'SlotDiagram';

/**
 * The recess the glyph sits in, sized to the glyph and nothing else.
 *
 * It hugs rather than fills so that the well is the same height on every slot of a row: the rail is
 * a sibling, not a child, so letting the recess take all the height going would draw it 12px taller
 * on the slots that happen to have no rail than on the ones that do. Whatever height is spare
 * collects outside it — see `SlotTarget`.
 */
const SlotInner = styled('div', {
  display: 'flex',
  borderRadius: `$${chassisVars.slot.radius}`,

  variants: {
    /** Which side of the chassis this slot is on, which is what the recess is tinted against. */
    face: {
      front: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
      rear: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
    },
    selected: {
      true: {
        background: colors.bgBrandLight,

        [darkThemeSelector]: {
          background: colors.bgBrandDark,
        },
      },
      false: {},
    },
  },
});
SlotInner.displayName = 'SlotInner';

/**
 * The slot's surface, and the click target when the port is one.
 *
 * `justifyContent: 'flex-end'` is what keeps a row level when only some of its slots carry a rail.
 * Rail, recess and caption pack against the inner edge, so every glyph on a row lands on the same
 * line and every number under it; whatever is left over — a missing rail, an outlet shorter than a
 * port — collects on the outer edge, where the rail would have been. Reserving the rail's height
 * instead would put that gap between the glyph and its number, which is the one place it reads as a
 * mistake.
 */
const SlotTarget = styled('div', {
  position: 'relative',
  zIndex: slotVars.zIndex.target,
  display: 'flex',
  justifyContent: 'flex-end',
  borderRadius: `$${chassisVars.slot.radius}`,
  outline: outlines.control,
  outlineColor: colors.strokeNeutralLight,
  transition: transitions.focusRing,

  [darkThemeSelector]: {
    outlineColor: colors.strokeNeutralDark,
  },

  /*
   * The target's own focus, not the shared `selectors.focus` — that one also matches
   * `:has(:focus-visible)`, so a slot whose glyph owns the click would ring twice: once around the
   * glyph and once around the slot holding it.
   */
  "&:focus-visible, &[data-focus-visible='true']": {
    outline: 'none',
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },

  variants: {
    face: {
      front: {
        background: colors.bgSurfaceLight,

        [darkThemeSelector]: {
          background: colors.bgSurfaceDark,
        },
      },
      rear: {
        background: colors.bgNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
        },
      },
    },
    selected: {
      true: {
        backgroundColor: colors.bgBrandLight,
        outlineColor: colors.strokeBrandLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgBrandDark,
          outlineColor: colors.strokeBrandDark,
        },
      },
      false: {},
    },
    orientation: {
      top: {
        flexDirection: 'column-reverse',
      },
      bottom: {
        flexDirection: 'column',
      },
    },
  },
});
SlotTarget.displayName = 'SlotTarget';

const SlotBadge = styled(BadgeGroup, {
  position: 'absolute',
  zIndex: slotVars.zIndex.badge,
  left: '50%',

  variants: {
    orientation: {
      top: {
        bottom: 0,
        transform: 'translate(-50%, 50%)',
      },
      bottom: {
        top: 0,
        transform: 'translate(-50%, -50%)',
      },
    },
  },
});
SlotBadge.displayName = 'SlotBadge';

const SlotContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  width: 'fit-content',
  minHeight: chassisVars.slot.height,

  variants: {
    hasIndicators: {
      true: {
        minHeight: chassisVars.slot.heightWithIndicators,
      },
      false: {},
    },
    muted: {
      true: {
        opacity: slotVars.mutedOpacity,
      },
      false: {},
    },
  },
});
SlotContainer.displayName = 'SlotContainer';

export type SlotOrientation = 'top' | 'bottom';

export type SlotFace = 'front' | 'rear';

export type SlotProps = {
  /** Badges pinned to the slot's inner edge, e.g. a VLAN tag. */
  badges?: ReactNode;
  /** What is plugged into this slot, as a chip on a tail off its outer edge. */
  connection?: SlotChipProps;
  /** The speed this slot's connection chip is coloured by. See `SlotChip`'s `speed`. */
  chipSpeed?: PortSpeed;
  /** The state that colour answers to first. See `SlotChip`'s `status`. */
  chipStatus?: PortStatus;
  /**
   * Which of its edge's two lanes this slot's connection chip hangs in. Owned by whoever lays the
   * slots out, since it alternates along the whole edge and a slot can't see its neighbours.
   */
  chipTier?: SlotChipTier;
  /** The glyph itself — a `Port` or an `Outlet`. */
  diagram: ReactNode;
  /**
   * Which side of the chassis this slot is drawn on, which sets the surface and recess it's tinted
   * against. `rear` is for slots inside a `ChassisRear`, whose panel is already a shade darker than
   * the card — front colours there would leave the slot looking lit from the wrong side.
   */
  face?: SlotFace;
  /**
   * Property icons for this slot, from `getPortIndicators`. Each one carries the position it draws
   * in: the caption row beside the number, or one of the rail's two ends along the outer edge.
   *
   * The rail costs its 12px only when something is actually placed on it, so a glyph with nothing to
   * advertise (a PDU outlet) and a port whose only property is PoE both keep the shorter footprint.
   * You don't have to level a row yourself: a slot pins its glyph and caption to its edges, so the
   * one port with an uplink makes room for the rail and its bare neighbours stay on its line.
   */
  indicators?: PortIndicator[];
  /** Overrides the number chip's text, for faceplates that name ports ('WAN0', 'OOB'). */
  label?: string | null;
  /** Dims the slot, for legend filtering. */
  muted?: boolean;
  /** What the number chip reads, and the slot's identity on the faceplate. */
  number?: number;
  onClick?: (event: any) => void;
  /** Which edge of the unit the slot sits on: `top` grows upward, `bottom` downward. */
  orientation?: SlotOrientation;
  selected?: boolean;
};

/**
 * One position on a faceplate: a glyph, an optional rail of property icons, the caption carrying the
 * number chip, and the chrome that hangs off it — badges and a connection chip.
 *
 * Children are ordered rail → glyph → caption, and `SlotTarget` reverses them for `orientation:
 * 'top'`. Either way the caption lands on the unit's inner edge and the rail on its outer one, with
 * the connection beyond the rail — so a two-row faceplate reads as one line of numbers down the
 * middle, everything a port advertises out at the rim.
 *
 * `indicators` is handed to both rows whole; each draws the placements it owns and ignores the rest,
 * so a caller never has to know which property belongs where.
 */
export function Slot({
  badges,
  chipSpeed,
  chipStatus,
  chipTier,
  connection,
  diagram,
  face = 'front',
  indicators,
  label,
  muted = false,
  number,
  onClick,
  orientation = 'bottom',
  selected,
}: SlotProps) {
  const hasRail = hasRailIndicators(indicators);
  const caption = label ?? number;
  const traceHighlight = useChassisTraceHighlight(connection?.traceId);

  return (
    <SlotContainer muted={muted} hasIndicators={hasRail} {...traceHighlight}>
      {/*
       * A connection hangs off the edge the glyph is on, away from the number chip — which is the
       * opposite of the slot's own orientation, since that names the direction the slot grows in.
       */}
      {connection && (
        <SlotChip
          orientation={orientation === 'top' ? 'bottom' : 'top'}
          speed={chipSpeed}
          status={chipStatus}
          tier={chipTier}
          {...connection}
          muted={muted}
        />
      )}
      {badges && (
        <SlotBadge orientation={orientation} relation="joined" size="small">
          {badges}
        </SlotBadge>
      )}
      {/*
       * The caption is the name: a clickable slot announces itself as the number it draws, which is
       * how the faceplate is read aloud anyway. The fallback is only for a slot that draws neither a
       * number nor a label — nothing else in the slot is text, so there would be no name at all.
       */}
      <SlotTarget
        aria-label={onClick && caption === undefined ? 'Slot' : undefined}
        onClick={onClick}
        tabIndex={onClick && 0}
        role={onClick && 'button'}
        face={face}
        selected={selected}
        orientation={orientation}
      >
        {indicators && hasRail && <SlotIndicators indicators={indicators} />}
        <SlotInner face={face} selected={selected}>
          <SlotDiagram>{diagram}</SlotDiagram>
        </SlotInner>
        <SlotCaption indicators={indicators} number={caption} selected={selected} />
      </SlotTarget>
    </SlotContainer>
  );
}

const SlotsLayout = styled('div', {
  gap: '$8',
  width: 'fit-content',

  variants: {
    align: {
      top: {},
      bottom: {
        alignSelf: 'flex-end',
      },
    },
    rows: {
      1: {
        display: 'flex',
        flexDirection: 'row',
      },
      2: {
        display: 'grid',
        gridAutoFlow: 'column',
        /*
         * Columns are pinned and rows are floors, and the asymmetry is the point.
         *
         * Port pitch is physical: every slot in a row occupies the same width whether or not its
         * number chip is a bare digit or 'LAN0', so a wide chip overflows its track rather than
         * shunting its neighbours. Height has no such constraint, and a quarter-turned outlet is 4px
         * taller than a port — pinned, it would push into the row beneath it.
         */
        gridTemplateColumns: `repeat(auto-fit, $${chassisVars.slot.width})`,
        gridTemplateRows: `repeat(2, minmax($${chassisVars.slot.height}, auto))`,
      },
    },
    /** Two-row grids floor their track height, so it has to clear the rail too. */
    hasIndicators: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      rows: 2,
      hasIndicators: true,
      css: {
        gridTemplateRows: `repeat(2, $${chassisVars.slot.heightWithIndicators})`,
      },
    },
  ],
});
SlotsLayout.displayName = 'SlotsLayout';

const SlotsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
});
SlotsContainer.displayName = 'SlotsContainer';

type SlotsProps = {
  align?: 'top' | 'bottom';
  /** Which side of the chassis these slots are on. Each slot may say otherwise for itself. */
  face?: SlotFace;
  label?: ReactNode;
  orientation?: 'top' | 'bottom';
  rows?: 1 | 2;
  slots: SlotProps[];
  start?: number;
} & ComponentProps<typeof SlotsContainer>;

/**
 * Which lane each slot's connection chip hangs in, alternating along each edge of the run.
 *
 * Counted per edge, not per slot: a two-row run flows by column, so consecutive slots land on
 * opposite edges and only every second one is a given chip's neighbour.
 */
function slotChipTiers(
  slots: SlotProps[],
  rows: 1 | 2,
  orientation?: SlotOrientation,
): SlotChipTier[] {
  const counts: Record<SlotOrientation, number> = { top: 0, bottom: 0 };

  return slots.map((slot, index) => {
    const edge =
      slot.orientation ?? orientation ?? (rows !== 1 && index % 2 !== 0 ? 'top' : 'bottom');
    const count = counts[edge];
    counts[edge] = count + 1;

    return count % 2 === 0 ? 'near' : 'far';
  });
}

/**
 * A run of slots numbered from `start`, for faceplates simple enough not to need a layout — an
 * access point's ports, a PDU's outlets.
 */
export function Slots({
  align,
  face,
  label,
  orientation,
  rows = 1,
  slots,
  start = 0,
  ...remaining
}: SlotsProps) {
  const tiers = slotChipTiers(slots, rows, orientation);

  return (
    <SlotsContainer {...remaining}>
      <SlotsLayout
        align={align}
        rows={rows}
        hasIndicators={slots.some((slot) => hasRailIndicators(slot.indicators))}
      >
        {slots.map((slot, index) => {
          const isOdd = index % 2 === 0;
          const key = start + index;
          return (
            <Slot
              key={`slot-${key}`}
              chipTier={tiers[index]}
              face={face}
              orientation={orientation ?? (rows !== 1 && !isOdd ? 'top' : undefined)}
              number={index + start}
              {...slot}
            />
          );
        })}
      </SlotsLayout>
    </SlotsContainer>
  );
}
